import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMoney, parseAmountToCents } from "@/lib/money";
import { toast } from "sonner";
import { Loader2, Copy, Check, Share2 } from "lucide-react";

const Request = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [amount, setAmount] = useState("25.00");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const cents = parseAmountToCents(amount);
    if (!cents) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!user) return;
    setSubmitting(true);
    const { data, error } = await supabase
      .from("money_requests")
      .insert({
        requester_id: user.id,
        amount_cents: cents,
        note: note.trim().slice(0, 140) || null,
      })
      .select("id")
      .single();
    setSubmitting(false);
    if (error || !data) {
      toast.error(error?.message ?? "Could not create request");
      return;
    }
    const url = `${window.location.origin}/r/${data.id}`;
    setLink(url);
    toast.success("Request link ready");
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const share = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Cash Isle request", text: `Pay me ${formatMoney(parseAmountToCents(amount) ?? 0)} on Cash Isle`, url: link });
      } catch {/* user cancelled */}
    } else {
      copy();
    }
  };

  const cents = parseAmountToCents(amount) ?? 0;

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <div className="flex justify-end -mt-1">
          <Link
            to="/creator-mode"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Creator Mode
          </Link>
        </div>
        <h1 className="text-center mt-3 font-display text-2xl font-bold">Request Money</h1>
      </BrandHeader>

      {!link ? (
        <form onSubmit={handleCreate} className="px-5 py-7 space-y-5 flex-1 animate-fade-in-up">
          <div>
            <Label htmlFor="amount" className="text-sm font-medium">Amount</Label>
            <div className="mt-2 flex items-center bg-secondary/60 rounded-xl px-5 h-16">
              <span className="font-display text-3xl font-bold text-muted-foreground mr-1">$</span>
              <input
                id="amount"
                inputMode="decimal"
                autoFocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent flex-1 font-display text-3xl font-bold outline-none w-full"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="note" className="text-sm font-medium">Note (optional)</Label>
            <Input
              id="note"
              placeholder="What's it for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={140}
              className="mt-2 h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
            />
          </div>

          <Button
            type="submit"
            variant="hero"
            size="xl"
            className="w-full mt-4"
            disabled={submitting || cents === 0}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : `Create request for ${formatMoney(cents)}`}
          </Button>
        </form>
      ) : (
        <div className="px-5 py-7 flex-1 flex flex-col items-center animate-fade-in-up">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Requesting</p>
          <p className="font-display text-5xl font-extrabold text-primary mt-1">
            {formatMoney(cents)}
          </p>
          {note && <p className="mt-2 text-sm text-muted-foreground text-center">"{note}"</p>}

          <div className="mt-7 w-full bg-secondary/60 rounded-xl px-4 py-3 text-sm break-all text-center">
            {link}
          </div>

          <div className="mt-5 w-full grid grid-cols-2 gap-3">
            <Button onClick={copy} variant="outline" size="xl" className="gap-2">
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={share} variant="hero" size="xl" className="gap-2">
              <Share2 className="h-5 w-5" /> Share
            </Button>
          </div>

          <Button
            onClick={() => { setLink(null); setAmount("25.00"); setNote(""); }}
            variant="ghost"
            className="mt-4"
          >
            Create another
          </Button>
        </div>
      )}

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default Request;
